import React from "react";
import { connect } from "react-redux";
import {
  withStyles,
  Theme,
  WithStyles,
  createStyles,
} from "@material-ui/core/styles";
import intl from "react-intl-universal";
import { Formik, Form, Field } from "formik";
import { Grid, Card, CardContent, Box } from "@material-ui/core";
import * as Yup from "yup";
import { useMutate } from "restful-react";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";

import { login, ILoginInput, ILoginResponse } from "apis";
import { TextField } from "components/TextField";
import { ButtonLink } from "components/ButtonLink";
import { routing } from "routing";
import { startLoadingAction, stopLoadingAction } from "components/ReduxSpinner";
import { setToken, getToken } from "utils/localStorage";

import Button from "../components/Button";
import Typography from "../components/Typography";
import ProductHeroLayout from "./ProductHeroLayout";

const backgroundImage = "/statistics-bk.jpg";

const { Urls } = routing;

const FIELD_EMAIL = "email";
const FIELD_PASSWORD = "password";

const styles = (theme: Theme) =>
  createStyles({
    background: {
      backgroundImage: `url(${backgroundImage})`,
      backgroundColor: "#7fc7d9", // Average color of the background image.
      backgroundPosition: "center",
    },
    button: {
      minWidth: 200,
    },
    h5: {
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(4),
      [theme.breakpoints.up("sm")]: {
        marginTop: theme.spacing(10),
      },
    },
    more: {
      marginTop: theme.spacing(2),
    },
    forgotPassword: {
      color: "#9fcbe0",
    },
  });

function ProductHero(
  props: WithStyles<typeof styles> & {
    startLoading: Function;
    stopLoading: Function;
  }
) {
  const { classes, startLoading, stopLoading } = props;
  const { mutate } = useMutate({
    verb: "POST",
    path: login(),
  });
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const enterSystem = () => {
    history.push(Urls.PLATFORM());
  };

  const renderLoginForm = () => {
    return (
      <Formik
        initialValues={{
          [FIELD_EMAIL]: "",
          [FIELD_PASSWORD]: "",
        }}
        onSubmit={async (values: any) => {
          startLoading();
          const input: ILoginInput = {
            email: values[FIELD_EMAIL],
            password: values[FIELD_PASSWORD],
          };
          mutate(input)
            .then((response: ILoginResponse) => {
              setToken(response.token);
              history.push(`${Urls.PLATFORM()}`);
            })
            .catch(() => {
              enqueueSnackbar(intl.get("LOGIN_FAILED_SNACKBAR"), {
                variant: "error",
              });
            })
            .finally(() => stopLoading());
        }}
        validationSchema={Yup.object().shape({
          [FIELD_EMAIL]: Yup.string()
            .email(intl.get("FORM_VALIDATION_INVALID_EMAIL"))
            .required(intl.get("FORM_VALIDATION_REQUIRED")),
          [FIELD_PASSWORD]: Yup.string().required(
            intl.get("FORM_VALIDATION_REQUIRED")
          ),
        })}
      >
        {() => {
          return (
            <Form>
              <Card>
                <CardContent>
                  <Grid container>
                    <Grid item xs={12}>
                      <Field
                        component={TextField}
                        name={FIELD_EMAIL}
                        label={`${intl.get("LOGIN_FORM_EMAIL")} *`}
                      />
                    </Grid>
                    <Box mt={2} />
                    <Grid item xs={12}>
                      <Box mt={1} />
                      <Field
                        component={TextField}
                        type="password"
                        name={FIELD_PASSWORD}
                        label={`${intl.get("LOGIN_FORM_PASSWORD")} *`}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <Box mt={2} display="flex" justifyContent="center">
                <Button
                  color="secondary"
                  variant="contained"
                  size="large"
                  className={classes.button}
                  type="submit"
                >
                  {intl.get("LOGIN_FORM_LOGIN")}
                </Button>
              </Box>
              <Box mt={1} display="flex" justifyContent="center">
                <ButtonLink
                  className={classes.button}
                  to={Urls.FORGOT_PASSWORD}
                >
                  <Typography className={classes.forgotPassword}>
                    {intl.get("LOGIN_FORM_FORGET_PASSWORD")}
                  </Typography>
                </ButtonLink>
              </Box>
            </Form>
          );
        }}
      </Formik>
    );
  };

  return (
    <ProductHeroLayout backgroundClassName={classes.background}>
      <img style={{ display: "none" }} src={backgroundImage} alt="" />
      <Typography color="inherit" align="center" variant="h2" marked="center">
        {intl.get("LANDING_DESC_TITLE")}
      </Typography>
      <Typography
        color="inherit"
        align="center"
        variant="h5"
        className={classes.h5}
      >
        {intl.get("LANDING_DESC_SUB_TITLE")}

        {getToken() ? (
          <Box mt={8}>
            <Button
              color="secondary"
              variant="contained"
              size="large"
              className={classes.button}
              onClick={enterSystem}
            >
              {intl.get("LOGIN_REDIRECT_DASHBOARD_BUTTON")}
            </Button>
          </Box>
        ) : (
          renderLoginForm()
        )}
      </Typography>
    </ProductHeroLayout>
  );
}

const mapDispatch = {
  startLoading: () => startLoadingAction,
  stopLoading: () => stopLoadingAction,
};

export default connect(null, mapDispatch)(withStyles(styles)(ProductHero));
